import {
    Box,
    Center,
    Drawer,
    Loader,
    NavLink,
    ScrollArea,
    Stack,
} from '@mantine/core';
import {
    IconCategory,
    IconChartAreaLine,
    IconFolder,
    IconFolders,
    IconHome,
    IconLayoutDashboard,
} from '@tabler/icons-react';
import { useState, type FC } from 'react';
import { Link } from 'react-router';
import { useSpaceSummaries } from '../../hooks/useSpaces';
import MantineIcon from '../common/MantineIcon';

type Props = {
    opened: boolean;
    onClose: () => void;
    activeProjectUuid: string | undefined;
};

/**
 * DL Fork: mobile drawer that surfaces Home + Browse navigation.
 * Notifications stay in the NavBar bell (see §8.2 rationale — keeping the
 * bell preserves the `setHasReadValidationNotification` side effect and avoids
 * re-implementing the dropdown content here).
 *
 * Browse mirrors the data from `BrowseMenu` but renders Mantine `NavLink`s
 * directly instead of lifting the `<Menu.Item>` JSX (which would require a
 * `<Menu>` ancestor).
 */
const MobileNavBarDrawer: FC<Props> = ({
    opened,
    onClose,
    activeProjectUuid,
}) => {
    // Mirror BrowseMenu's lazy fetch: only request spaces once the Browse
    // section has been expanded at least once.
    const [browseExpanded, setBrowseExpanded] = useState(false);
    const { data: spaces, isInitialLoading: spacesLoading } = useSpaceSummaries(
        activeProjectUuid ?? '',
        true,
        {
            select: (data) => data.filter((space) => !space.parentSpaceUuid),
            enabled: browseExpanded && !!activeProjectUuid,
        },
    );

    const homeUrl = activeProjectUuid
        ? `/projects/${activeProjectUuid}/home`
        : '/';

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="left"
            size="80%"
            withCloseButton
            zIndex={500}
            padding="md"
            title="Menu"
        >
            <Stack gap="xs">
                <NavLink
                    component={Link}
                    to={homeUrl}
                    onClick={onClose}
                    label="Home" leftSection={<MantineIcon icon={IconHome} />}
                />

                <NavLink
                    label="Browse" leftSection={<MantineIcon icon={IconCategory} />}
                    childrenOffset={28}
                    opened={browseExpanded}
                    onClick={() => setBrowseExpanded((v) => !v)}
                >
                    {activeProjectUuid ? (
                        <>
                            <NavLink
                                component={Link}
                                to={`/projects/${activeProjectUuid}/spaces`}
                                onClick={onClose}
                                label="All Spaces" leftSection={<MantineIcon icon={IconFolders} />}
                            />
                            <NavLink
                                component={Link}
                                to={`/projects/${activeProjectUuid}/dashboards`}
                                onClick={onClose}
                                label="All dashboards"
                                leftSection={
                                    <MantineIcon icon={IconLayoutDashboard} />
                                }
                            />
                            <NavLink
                                component={Link}
                                to={`/projects/${activeProjectUuid}/saved`}
                                onClick={onClose}
                                label="All saved charts" leftSection={<MantineIcon icon={IconChartAreaLine} />}
                            />

                            {spacesLoading ? (
                                <Center my="sm">
                                    <Loader size="sm" color="gray" />
                                </Center>
                            ) : null}

                            {spaces && spaces.length > 0 ? (
                                <ScrollArea
                                    variant="primary"
                                    scrollbarSize={6}
                                    type="hover"
                                >
                                    <Box mah={300}>
                                        {spaces
                                            .slice()
                                            .sort((a, b) =>
                                                a.name.localeCompare(b.name),
                                            )
                                            .map((space) => (
                                                <NavLink
                                                    key={space.uuid}
                                                    component={Link}
                                                    to={`/projects/${activeProjectUuid}/spaces/${space.uuid}`}
                                                    onClick={onClose}
                                                    label={space.name}
                                                    leftSection={
                                                        <MantineIcon
                                                            icon={IconFolder}
                                                        />
                                                    }
                                                />
                                            ))}
                                    </Box>
                                </ScrollArea>
                            ) : null}
                        </>
                    ) : null}
                </NavLink>
            </Stack>
        </Drawer>
    );
};

export default MobileNavBarDrawer;
